/**
 * Shared frontend source of truth for DashONE user scoping + role vocabulary,
 * consumed by both apps/web and apps/admin. This file owns the product-scope
 * module catalog; the canonical role display vocabulary lives in `roles.ts`
 * and is re-exported below.
 */
export {
  ROLE_DISPLAY_NAMES,
  ROLE_STYLES,
  getRoleDisplayName,
  getRoleStyle,
  type RoleStyle,
} from "./roles";

/**
 * Single source of truth for the DashONE product-scope modules — the dashboards
 * a user is granted per-market access to via
 * `userData.id_client[workspace].product_scopes`.
 *
 * Consumed by both frontends: apps/web (invite/edit modals, sidebar nav, route
 * guards) and apps/admin (User Scope Management, invite/create user modals). The
 * matching backend key list lives in `apps/api/src/utils/auth.py`
 * (`MODULE_SCOPE_KEYS`) and is held in lockstep by a contract test — keep the
 * `key: "..."` literals below in a form that test can parse.
 *
 * DashAI is intentionally absent (available to every authenticated user).
 */

export interface ModuleScope {
  /** Stable key stored in `product_scopes` and enforced on the backend. */
  key: string;
  /** Human label shown in scope editors and nav. */
  label: string;
  /** Route the module's page is mounted at. */
  route: string;
  /** Accent color for the admin product-scope editor swatch. */
  color: string;
  /**
   * Workspaces this module is offered in. Omitted = available everywhere; when
   * set, scope editors show the module only for these workspace keys.
   */
  availableIn?: string[];
}

/** Ordered list of independently-grantable modules. */
export const MODULE_SCOPES: ModuleScope[] = [
  { key: "market_overview", label: "Market Overview", route: "/market_overview", color: "#1890ff" },
  { key: "market_deepdive", label: "Market Deepdive", route: "/market_deepdive", color: "#52c41a" },
  { key: "execution_view", label: "Execution View", route: "/execution_view", color: "#faad14" },
  { key: "physical_location", label: "Physical Location", route: "/physical_location", color: "#13c2c2" },
  { key: "physical_franchise", label: "Physical Franchise", route: "/physical_franchise", color: "#eb2f96" },
];

/** Module keys only, e.g. for building empty scope objects. */
export const MODULE_SCOPE_KEYS: string[] = MODULE_SCOPES.map((m) => m.key);

/** Map of route -> module key, for guarding routes. */
export const ROUTE_TO_MODULE: Record<string, string> = MODULE_SCOPES.reduce(
  (acc, m) => {
    acc[m.route] = m.key;
    return acc;
  },
  {} as Record<string, string>,
);

/** A `product_scopes` object with every module present and no markets selected. */
export const emptyProductScopes = (): Record<string, string[]> =>
  MODULE_SCOPE_KEYS.reduce(
    (acc, key) => {
      acc[key] = [];
      return acc;
    },
    {} as Record<string, string[]>,
  );

/** A `product_scopes` object granting every module the full `markets` list. */
export const allProductScopes = (markets: string[] = []): Record<string, string[]> =>
  MODULE_SCOPE_KEYS.reduce(
    (acc, key) => {
      acc[key] = [...markets];
      return acc;
    },
    {} as Record<string, string[]>,
  );

/**
 * Modules offered in a given workspace: those with no `availableIn` (universal),
 * plus those whose `availableIn` lists the workspace key. Pure over the passed
 * list so callers can filter any subset.
 */
export const filterModulesForWorkspace = (
  modules: ModuleScope[],
  workspaceKey: string | null | undefined,
): ModuleScope[] =>
  modules.filter(
    (m) => !m.availableIn || (workspaceKey != null && m.availableIn.includes(workspaceKey)),
  );

/** Convenience: `filterModulesForWorkspace` applied to the full catalog. */
export const modulesForWorkspace = (workspaceKey: string | null | undefined): ModuleScope[] =>
  filterModulesForWorkspace(MODULE_SCOPES, workspaceKey);
